import React from 'react';

import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { badgeCardTypeOptions, guestReasonOptions } from 'src/features/cards/utils/constants';
import { cardMessages } from 'src/features/cards/utils/messages';
import { useListTotalValidationsStatistics } from 'src/features/statistics/api/useListTotalValidationsStatistics';
import { useListUsers } from 'src/features/statistics/api/useListUsers';
import { useListValidationDetailsStatistics } from 'src/features/statistics/api/useListValidationDetailsStatistics';
import { CellValidationReason } from 'src/features/statistics/components/CellValidationReason';
import { CustomDataTableControls } from 'src/features/statistics/components/CustomDataTableControls';
import StatisticsCards from 'src/features/statistics/components/StatisticsCards';
import { statisticsMessages, TOTAL_SPACES_NAME } from 'src/features/statistics/utils';
import useFilters from 'src/hooks/useFilters';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import {
    SectionBlock,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSeparator,
    SectionBlockSubtitle,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellPrice } from 'src/registry/components/table/cells/CellPrice';
import DataTableDateDropdown from 'src/registry/components/table/DataTableDateDropdown';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import { Button } from 'src/registry/ui/Button';
import { useTitleStore } from 'src/stores/useTitleStore';

import type { StatisticOperationDetails } from 'src/features/statistics/types';

export default function OperationValidations() {
    const { parkingId, parkingCurrency } = useParking();

    const { setTitle, removeTitle } = useTitleStore();
    const [dateTime, setDateTime] = React.useState(() => {
        const currentDate = new Date();
        const today = new Date(currentDate.getTime());
        today.setHours(23);
        today.setMinutes(59);
        today.setSeconds(59);
        const lastWeek = new Date(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7).getTime());
        return { afterDate: lastWeek, beforeDate: today };
    });

    React.useEffect(() => {
        setTitle(formatMessage(statisticsMessages.validationOperations));
        return () => removeTitle();
    }, []);

    const { formatMessage, formatNumber } = useIntl();

    const { data: users } = useListUsers({ parkingId });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'enter_datetime', desc: false }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'none' });
    const DataTableControls = React.useMemo(
        () =>
            CustomDataTableControls(
                <DataTableDateDropdown
                    setValue={setDateTime}
                    inline
                    afterDate={dateTime.afterDate}
                    beforeDate={dateTime.beforeDate}
                />,
            ),
        [setDateTime],
    );
    const columnHelper = createColumnHelper<StatisticOperationDetails>();

    const { data: totalValidations } = useListTotalValidationsStatistics({
        parkingId,
        to_date: dateTime.beforeDate.toISOString(),
        from_date: dateTime.afterDate.toISOString(),
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
    });

    const {
        data: validationDetails,
        isError,
        isLoading,
    } = useListValidationDetailsStatistics({
        parkingId,
        to_date: dateTime.beforeDate.toISOString(),
        from_date: dateTime.afterDate.toISOString(),
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
    });

    const cardData = React.useMemo(() => {
        if (!totalValidations) return [{ title: formatMessage(statisticsMessages.allOperations), value: 0 }];
        return totalValidations.results.map((validation) => {
            return {
                title:
                    validation.result_type === TOTAL_SPACES_NAME
                        ? formatMessage(statisticsMessages.allOperations)
                        : validation.operator_name,
                value: formatNumber(validation.due_amount / 100, {
                    style: 'currency',
                    currency: parkingCurrency,
                }),
            };
        });
    }, [totalValidations]);

    const columns = [
        columnHelper.accessor('operator_name', {
            header: DataTableColumnHeader,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(cardMessages.cardTypeOperator),
                options: users?.results.map((user) => ({ value: user.id, label: user.name })),
            },
            id: 'operator_id',
        }),
        columnHelper.accessor('identifier', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(cardMessages.cardNumber),
            },
        }),
        columnHelper.accessor('identifier_type', {
            header: DataTableColumnHeader,
            cell: (props) => <CellBadge badgeOptions={badgeCardTypeOptions} {...props} />,
            enableHiding: true,
            meta: {
                title: formatMessage(cardMessages.cardType),
            },
        }),
        columnHelper.accessor('event_datetime', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableHiding: true,
            meta: {
                title: formatMessage(statisticsMessages.date),
            },
        }),
        columnHelper.accessor('due_amount', {
            header: DataTableColumnHeader,
            cell: CellPrice,
            enableHiding: true,
            meta: {
                title: formatMessage(cardMessages.dueAmount),
            },
        }),
        columnHelper.accessor('charge_plan_name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(cardMessages.chargePlan),
            },
        }),
        columnHelper.accessor('validation_reason', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: (props) => {
                const reason = guestReasonOptions.find((reason) => reason.value === props.row.original.validation_reason);
                return <CellValidationReason
                    {...props}
                    value={reason
                        ? formatMessage(reason.label)
                        : props.row.original.validation_reason}
                />
            },
            meta: {
                title: formatMessage(statisticsMessages.validationReason),
                inputType: INPUT_TYPE.TEXT,
                isLongText: true,
            },
            id: 'reason',
        }),
    ];

    return (
        <SectionBlock>
            <Button asChild className="-mt-6 place-self-start p-0" variant="link">
                <Link to="/statistics/reports">
                    <FontAwesomeIcon icon={faChevronLeft} className="opacity-50" />
                    {formatMessage(statisticsMessages.backToAllReports)}
                </Link>
            </Button>
            <SectionBlockHeader>
                <SectionBlockHead>
                    <SectionBlockTitle>{formatMessage(statisticsMessages.allValidations)}</SectionBlockTitle>
                    <SectionBlockSubtitle>
                        {formatMessage(statisticsMessages.allValidationsSubtitle)}
                    </SectionBlockSubtitle>
                </SectionBlockHead>
            </SectionBlockHeader>
            <SectionBlockSeparator />
            <StatisticsCards cardData={cardData} />
            <DataTable
                columns={columns}
                data={validationDetails}
                pagination={pagination}
                sorting={sorting}
                filters={filters}
                search={search}
                semantics={{
                    title: formatMessage(statisticsMessages.operations),
                    entryName: formatMessage(statisticsMessages.operation),
                    entryNames: formatMessage(statisticsMessages.operations),
                    placeholder: formatMessage(cardMessages.cardTypeOperator),
                }}
                isError={isError}
                isLoading={isLoading}
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
            />
        </SectionBlock>
    );
}
